import { useState, useEffect } from 'react';
import { useCallPanel } from '../hooks/useCallPanel';
import { CallPanel as CallPanelType } from '../types';
import { Monitor, Volume2, AlertTriangle } from 'lucide-react';

export default function CallDisplay() {
  const { calls, loading } = useCallPanel();
  const [currentCall, setCurrentCall] = useState<CallPanelType | null>(null);
  const [lastCalls, setLastCalls] = useState<CallPanelType[]>([]);

  useEffect(() => {
    // Encontrar a chamada atual (status 'chamando')
    const callingNow = calls.find(call => call.status === 'chamando');
    
    if (callingNow && (!currentCall || currentCall.id !== callingNow.id)) {
      setCurrentCall(callingNow);
      
      // Tocar som de chamada
      playCallSound();
      
      // Adicionar às últimas chamadas
      setLastCalls(prev => {
        const updated = [callingNow, ...prev.filter(call => call.id !== callingNow.id)];
        return updated.slice(0, 5); // Manter apenas as 5 últimas
      });
    }
    
    // Se não há chamada ativa, limpar a chamada atual após um tempo
    if (!callingNow && currentCall) {
      setTimeout(() => {
        setCurrentCall(null);
      }, 5000); // Manter por 5 segundos após parar de chamar
    }
  }, [calls, currentCall]);

  const playCallSound = () => {
    // Som de chamada tocará apenas no display TV
    console.log('🔊 Tocando som de chamada no display TV...');
    
    if ('AudioContext' in window || 'webkitAudioContext' in window) {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContext();
      
      // Sequência de tons mais elaborada para o display
      const frequencies = [800, 1000, 800, 1000, 800];
      let time = audioContext.currentTime;
      
      frequencies.forEach((freq, index) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(freq, time);
        gainNode.gain.setValueAtTime(0.1, time);
        gainNode.gain.exponentialRampToValueAtTime(0.01, time + 0.5);
        
        oscillator.start(time);
        oscillator.stop(time + 0.5);
        
        time += 0.7; // Intervalo maior entre os tons
      });
      
      // Repetir o som 3 vezes
      setTimeout(() => {
        if (currentCall && currentCall.status === 'chamando') {
          playCallSound();
        }
      }, 4000);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgente':
        return 'text-red-600 bg-red-100';
      case 'preferencial':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-blue-600 bg-blue-100';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'urgente':
        return 'URGENTE';
      case 'preferencial':
        return 'PREFERENCIAL';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-medical-50 to-medical-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-medical-50 to-medical-100 p-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <Monitor className="h-12 w-12 text-medical-600 mr-4" />
          <h1 className="text-4xl font-bold text-medical-800">Painel de Chamadas</h1>
        </div>
        <div className="flex items-center justify-center text-gray-600">
          <Volume2 className="h-5 w-5 mr-2" />
          <span className="text-lg">Sistema de Chamadas Ativo</span>
        </div>
      </div>

      {/* Current Call */}
      {currentCall ? (
        <div className="mb-12">
          <div className={`bg-white rounded-2xl shadow-2xl p-12 text-center border-4 transition-all duration-500 ${
            currentCall.priority === 'urgente' 
              ? 'border-red-500 animate-pulse' 
              : currentCall.priority === 'preferencial'
              ? 'border-yellow-500'
              : 'border-medical-500'
          }`}>
            {/* Priority Badge */}
            {currentCall.priority !== 'normal' && (
              <div className={`inline-flex items-center px-6 py-2 rounded-full text-lg font-bold mb-6 ${getPriorityColor(currentCall.priority)}`}>
                {currentCall.priority === 'urgente' && <AlertTriangle className="h-6 w-6 mr-2" />}
                {getPriorityText(currentCall.priority)}
              </div>
            )}
            
            <div className="space-y-6">
              <h2 className="text-6xl font-bold text-gray-800 mb-4">CHAMANDO</h2>
              
              <div className="bg-medical-50 rounded-xl p-8 mb-6">
                <div className="text-5xl font-bold text-medical-800 mb-4">
                  SENHA: {currentCall.ticketNumber}
                </div>
                <div className="text-4xl font-semibold text-gray-700 mb-2">
                  {currentCall.patientName}
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-2xl text-gray-600">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="font-semibold text-gray-800">Serviço</div>
                  <div>{currentCall.service}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="font-semibold text-gray-800">Local</div>
                  <div>{currentCall.room}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="font-semibold text-gray-800">Profissional</div>
                  <div>Dr(a). {currentCall.professional}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center border-2 border-gray-200">
            <Monitor className="mx-auto h-24 w-24 text-gray-400 mb-6" />
            <h2 className="text-4xl font-bold text-gray-400 mb-4">Aguardando Chamadas</h2>
            <p className="text-xl text-gray-500">O sistema está pronto para receber chamadas</p>
          </div>
        </div>
      )}

      {/* Queue Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <div className="text-3xl font-bold text-blue-600 mb-2">
            {calls.filter(call => call.status !== 'concluido').length}
          </div>
          <div className="text-lg text-gray-600">Na Fila</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <div className="text-3xl font-bold text-green-600 mb-2">
            {calls.filter(call => call.status === 'atendendo').length}
          </div>
          <div className="text-lg text-gray-600">Atendendo</div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <div className="text-3xl font-bold text-gray-600 mb-2">
            {calls.filter(call => call.status === 'concluido').length}
          </div>
          <div className="text-lg text-gray-600">Concluídos</div>
        </div>
      </div>

      {/* Recent Calls */}
      {lastCalls.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Últimas Chamadas</h3>
          <div className="space-y-3">
            {lastCalls.slice(0, 3).map((call, index) => (
              <div key={call.id} className={`flex items-center justify-between p-4 rounded-lg ${
                index === 0 ? 'bg-medical-50 border border-medical-200' : 'bg-gray-50'
              }`}>
                <div className="flex items-center space-x-4">
                  <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                    call.priority === 'urgente' ? 'bg-red-100 text-red-800' :
                    call.priority === 'preferencial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {call.ticketNumber}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-800">{call.patientName}</div>
                    <div className="text-sm text-gray-600">{call.service}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-800">{call.room}</div>
                  <div className="text-sm text-gray-600">Dr(a). {call.professional}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center mt-8 text-gray-500">
        <p className="text-lg">
          {new Date().toLocaleDateString('pt-BR', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })} - {new Date().toLocaleTimeString('pt-BR')}
        </p>
      </div>
    </div>
  );
}